import type { VaultData, VaultContact, VaultContactIdentity, VaultDeviceEntry, SetVaultPayload, EncryptedBlob, VaultMkTransferRequest, VaultMkTransferResponse, MasterKeyStatus } from '@shared/types';
import { APIError, vaultAPI, configAPI } from '../api/client';
import { signRequestWithKey } from '../crypto/signing';
import { encryptWithMasterKey, decryptWithMasterKey } from '../crypto/keys';
import type { SigningKey } from '../crypto/ed25519Provider';
// encryptWithMasterKey / decryptWithMasterKey work with any AES-GCM CryptoKey, not only masterKey.
import { useVaultStore } from '../store/vaultStore';
import { loadLocalVault, saveLocalVault } from '../crypto/storage';
import { t } from '../i18n';
import { requestConfirmation } from './feedback';
import { useAuthStore } from '../store/authStore';
import type { Identity, Device } from '../store/authStore';
import { updateIdentityMeta } from '../services/keyStatePersistence';

/**
 * Prepare vault for syncing to a specific server
 *
 * CRITICAL for multi-tenancy privacy:
 * - Removes localData (device-specific data that shouldn't be synced)
 * - Filters contacts: only include contacts that have identity on this server
 * - For each contact: remove identities from other servers (privacy!)
 *
 * Example:
 *   Local vault has:
 *     - Contact "Mom" with identities on server1.com and server2.com
 *     - Contact "Dad" with identity only on server1.com
 *
 *   prepareVaultForSync(vault, "server1.com") returns:
 *     - Contact "Mom" with ONLY server1.com identity
 *     - Contact "Dad" with ONLY server1.com identity
 *
 *   server2.com NEVER sees "Dad" or "Mom"'s server1.com identity!
 *
 * @param vault - Full local vault with all contacts
 * @param serverId - Target server ID (e.g., "server1.com")
 * @returns Filtered vault containing only this server's data
 */
function prepareVaultForSync(vault: VaultData, serverId: string): VaultData {
  // Remove localData
  const syncableData = { ...vault };
  delete (syncableData as Partial<VaultData>).localData;

  // Filter contacts: only those with identity on this server
  const filteredContacts: Record<string, VaultContact> = {};

  for (const [contactId, contact] of Object.entries(syncableData.contacts)) {
    // Check if contact has identity on this server
    const serverIdentities = contact.identities.filter(
      (identity) => identity.serverId === serverId
    );

    if (serverIdentities.length > 0) {
      // Include contact but ONLY with identities from this server
      filteredContacts[contactId] = {
        ...contact,
        identities: serverIdentities
      };
    }
  }

  // Return vault with filtered contacts
  // devices section is device-global (not circle-specific), include as-is
  return {
    ...syncableData,
    contacts: filteredContacts
  };
}

/**
 * Merge the `devices` section from two vault sources.
 *
 * Rules (see spec 03A.3.3):
 * - Same deviceId from two sources → keep the entry with the later updatedAt
 *   (client timestamp of the originating device; only used to resolve
 *   "same device seen through different circles" inconsistencies).
 * - Conflict detection (two different devices both 'created_here') is NOT
 *   resolved here — both are preserved so the caller can surface it to the user.
 */
function mergeMkConflictResolution(
  local: VaultData['mkConflictResolution'],
  incoming: VaultData['mkConflictResolution']
): VaultData['mkConflictResolution'] {
  if (!local && !incoming) return undefined;
  if (!local) return incoming;
  if (!incoming) return local;
  return incoming.resolvedAt > local.resolvedAt ? incoming : local;
}

function mergePendingMkRequests(
  local: Record<string, VaultMkTransferRequest> | undefined,
  incoming: Record<string, VaultMkTransferRequest> | undefined
): Record<string, VaultMkTransferRequest> | undefined {
  if (!local && !incoming) return undefined;
  const result = { ...local };
  for (const [deviceId, req] of Object.entries(incoming ?? {})) {
    const existing = result[deviceId];
    if (!existing || req.requestedAt > existing.requestedAt) {
      result[deviceId] = req;
    }
  }
  return result;
}

function mergePendingMkResponses(
  local: Record<string, VaultMkTransferResponse> | undefined,
  incoming: Record<string, VaultMkTransferResponse> | undefined
): Record<string, VaultMkTransferResponse> | undefined {
  if (!local && !incoming) return undefined;
  const result = { ...local };
  for (const [deviceId, resp] of Object.entries(incoming ?? {})) {
    const existing = result[deviceId];
    if (!existing || resp.respondedAt > existing.respondedAt) {
      result[deviceId] = resp;
    }
  }
  return result;
}

function mergeDevices(
  local: Record<string, VaultDeviceEntry> | undefined,
  incoming: Record<string, VaultDeviceEntry> | undefined
): Record<string, VaultDeviceEntry> {
  const result: Record<string, VaultDeviceEntry> = { ...local };
  for (const [deviceId, incomingEntry] of Object.entries(incoming ?? {})) {
    const existing = result[deviceId];
    if (!existing || incomingEntry.updatedAt > existing.updatedAt) {
      result[deviceId] = incomingEntry;
    }
  }
  return result;
}

/**
 * Merge vault from another device/server
 * - Contacts with same contactId: merge identities
 * - New contactId: add as new contact
 * - Groups: merge by groupId
 * - Preferences: incoming overrides local (last-write-wins)
 * - localData: preserve local (not merged)
 */
function mergeVaultData(
  local: VaultData | null,
  incoming: VaultData
): VaultData {
  if (!local) {
    // No local vault, use incoming as-is (but normalize duplicates defensively)
    return normalizeVault({
      ...incoming,
      meta: { updatedAt: new Date().toISOString() }
    });
  }

  const merged: VaultData = {
    version: Math.max(local.version, incoming.version),
    contacts: { ...local.contacts },
    groups: { ...local.groups, ...incoming.groups },
    preferences: { ...local.preferences, ...incoming.preferences },
    serverRegistrations: {
      ...local.serverRegistrations,
      ...incoming.serverRegistrations
    },
    devices: mergeDevices(local.devices, incoming.devices),
    pendingMkRequests: mergePendingMkRequests(local.pendingMkRequests, incoming.pendingMkRequests),
    pendingMkResponses: mergePendingMkResponses(local.pendingMkResponses, incoming.pendingMkResponses),
    mkConflictResolution: mergeMkConflictResolution(local.mkConflictResolution, incoming.mkConflictResolution),
    circleDisplayNames: { ...local.circleDisplayNames, ...incoming.circleDisplayNames },
    circleOwnerIdentityIds: { ...local.circleOwnerIdentityIds, ...incoming.circleOwnerIdentityIds },
    circleOwnerIdentityNames: { ...local.circleOwnerIdentityNames, ...incoming.circleOwnerIdentityNames },
    localData: local.localData, // Preserve local data
    meta: {
      updatedAt: new Date().toISOString()
    }
  };

  // Merge contacts
  for (const [contactId, incomingContact] of Object.entries(incoming.contacts)) {
    const localContact = local.contacts[contactId];

    if (!localContact) {
      // New contact, add as-is
      merged.contacts[contactId] = incomingContact;
    } else {
      // Contact exists, merge identities
      merged.contacts[contactId] = mergeContact(localContact, incomingContact);
    }
  }

  return normalizeVault(merged);
}

function getIdentityKey(identity: VaultContactIdentity): string {
  return `${identity.serverId}:${identity.identityId}`;
}

/**
 * Merge contacts that reference the same identity (serverId + identityId).
 * This prevents duplicates when the same contact is added twice with different contactIds
 * (e.g., after a page reload when local vault wasn't loaded yet).
 */
function normalizeVault(vault: VaultData): VaultData {
  const keyToContactId = new Map<string, string>();
  const normalizedContacts: Record<string, VaultContact> = {};

  for (const contact of Object.values(vault.contacts)) {
    const sanitizedContact = contact;
    // If any identity key already exists, merge into that existing contactId.
    let targetContactId: string | null = null;
    for (const identity of sanitizedContact.identities) {
      const key = getIdentityKey(identity);
      const existing = keyToContactId.get(key);
      if (existing) {
        targetContactId = existing;
        break;
      }
    }

    if (!targetContactId) {
      targetContactId = contact.contactId;
    }

    const existingContact = normalizedContacts[targetContactId];
    if (!existingContact) {
      normalizedContacts[targetContactId] = sanitizedContact;
    } else {
      normalizedContacts[targetContactId] = mergeContact(existingContact, sanitizedContact);
    }

    // Update mapping for all identities of the merged contact
    for (const identity of normalizedContacts[targetContactId].identities) {
      keyToContactId.set(getIdentityKey(identity), targetContactId);
    }
  }

  return {
    ...vault,
    contacts: normalizedContacts
  };
}

/**
 * Merge two versions of the same contact
 * Combines identities from both, avoiding duplicates
 */
function mergeContact(
  local: VaultContact,
  incoming: VaultContact
): VaultContact {
  const localSanitized = local;
  const incomingSanitized = incoming;

  // Use the displayName from whichever side was renamed more recently.
  // displayNameUpdatedAt is set explicitly on rename; fall back to createdAt for
  // contacts created before this field existed (treats initial name as set at creation time).
  const localNameTs = new Date(localSanitized.displayNameUpdatedAt || localSanitized.createdAt).getTime();
  const incomingNameTs = new Date(incomingSanitized.displayNameUpdatedAt || incomingSanitized.createdAt).getTime();
  const useIncomingName = incomingNameTs > localNameTs;

  // Merge identities, avoiding duplicates
  const identitiesMap = new Map<string, VaultContactIdentity>();

  // Add local identities
  for (const identity of localSanitized.identities) {
    const key = `${identity.serverId}:${identity.identityId}`;
    identitiesMap.set(key, identity);
  }

  // Add/update with incoming identities
  for (const identity of incomingSanitized.identities) {
    const key = `${identity.serverId}:${identity.identityId}`;
    const existing = identitiesMap.get(key);

    if (!existing) {
      // New identity
      identitiesMap.set(key, identity);
    } else {
      // Identity exists, keep the newer identity (addedAt)
      const useIncoming = new Date(identity.addedAt) > new Date(existing.addedAt);
      identitiesMap.set(key, useIncoming ? identity : existing);
    }
  }

  return {
    contactId: localSanitized.contactId, // Same ID
    displayName: useIncomingName ? incomingSanitized.displayName : localSanitized.displayName,
    identities: Array.from(identitiesMap.values()),
    groupId: incomingSanitized.groupId || localSanitized.groupId,
    createdAt: localSanitized.createdAt // Keep original creation time
  };
}

/**
 * Save vault with conflict resolution
 * Implements get-before-save pattern with automatic merging
 *
 * @param serverId - Server ID to sync with (filters vault to this server's contacts only)
 * @param serverUrl - Base URL for the target server (e.g., https://example.com)
 * @param deviceId - Current device ID
 * @param privateKeyBytes - Device private key for signing
 * @param masterKey - Master key for encryption/decryption
 * @param maxRetries - Maximum number of retry attempts (default: 3)
 * @returns true if successful, false if failed
 */
async function saveVaultWithConflictResolution(
  identityId: string,
  serverId: string,
  serverUrl: string,
  deviceId: string,
  signingKey: SigningKey,
  vaultKey: CryptoKey,
  maxRetries: number = 3
): Promise<boolean> {
  let attempts = 0;
  let overwriteConfirmedForThisSave = false;

  while (attempts < maxRetries) {
    attempts++;

    try {
      const vaultStore = useVaultStore.getState();
      const localVault = vaultStore.vaultData;
      const localRevision = vaultStore.revisionsByIdentityId[identityId] || 0;

      if (!localVault) {
        console.error('No local vault to save');
        return false;
      }

      // Prepare vault for sync (filter by server, remove localData)
      const syncableVault = prepareVaultForSync(localVault, serverId);

      // Step 1: Get current vault from server
      const getRequest = await signRequestWithKey('vault:get', {}, deviceId, signingKey);
      const getResponse = await vaultAPI.get(getRequest, serverUrl);

      if (getResponse.status === 'error') {
        throw new Error(getResponse.error?.message || 'Failed to fetch vault');
      }

      const serverRevision = getResponse.result?.revision || 0;
      let vaultToSave = syncableVault;

      // If the server already has a vault but we cannot decrypt it with the current master key,
      // we must NOT silently overwrite it. Ask for explicit user confirmation.
      if (getResponse.result?.vault) {
        let decryptable = true;
        try {
          await decryptWithMasterKey(vaultKey, getResponse.result.vault);
        } catch {
          decryptable = false;
        }

        if (!decryptable) {
          if (!overwriteConfirmedForThisSave) {
            const ok = await requestConfirmation({
              title: t('vault.confirmOverwrite'),
              variant: 'danger'
            });
            if (!ok) {
              return false;
            }
            overwriteConfirmedForThisSave = true;
          }

          // Skip merge (impossible) and proceed to overwrite server with current local vault.
          // expectedRevision will still protect against races.
          vaultToSave = syncableVault;
        }
      }

      // Step 2: Check if server has newer version
      // If the server vault is not decryptable with our master key, we cannot merge.
      if (serverRevision > localRevision && getResponse.result?.vault) {

        // Decrypt server vault
        let decryptedServerVault: string;
        try {
          decryptedServerVault = await decryptWithMasterKey(vaultKey, getResponse.result.vault);
        } catch {
          // Not decryptable with this vault key; overwrite path is handled above.
          decryptedServerVault = '';
        }
        if (decryptedServerVault) {
          const serverVault: VaultData = JSON.parse(decryptedServerVault);

          // Merge server vault into the FULL local vault (not the server-filtered syncableVault)
          // to avoid dropping contacts from other servers.
          const mergedFullVault = mergeVaultData(localVault, serverVault);
          // What we send to this server is still filtered to its own contacts only.
          vaultToSave = prepareVaultForSync(mergedFullVault, serverId);

          // Update local store with merged FULL vault (preserving localData)
          const mergedWithLocalData = {
            ...mergedFullVault,
            localData: localVault.localData
          };

          const nextRevisions = {
            ...vaultStore.revisionsByIdentityId,
            [identityId]: serverRevision
          };
          vaultStore.loadVault(mergedWithLocalData, nextRevisions, true);
        }
      }

      // Step 3: Encrypt and save vault with expectedRevision
      const encryptedVault = await encryptWithMasterKey(
        vaultKey,
        JSON.stringify(vaultToSave)
      );

      const setPayload: SetVaultPayload = {
        encryptedVault,
        expectedRevision: serverRevision
      };

      const setRequest = await signRequestWithKey('vault:set', setPayload, deviceId, signingKey);
      const setResponse = await vaultAPI.set(setRequest, serverUrl);

      // Step 4: Handle response
      if (setResponse.status === 'ok' && setResponse.result) {
        // Success! Update local revision; the outer multi-circle save marks
        // everything clean only once all requested writes have completed.
        // Use the current full vault from the store (may have been updated during merge above)
        // rather than vaultToSave, which is server-filtered and would drop contacts from other servers.
        const currentFullVault = useVaultStore.getState().vaultData ?? localVault;
        const nextRevisions = {
          ...useVaultStore.getState().revisionsByIdentityId,
          [identityId]: setResponse.result.revision
        };
        const nextVault: VaultData = { ...currentFullVault, localData: localVault.localData };
        // Keep the pending marker until the caller has saved every requested
        // circle; one successful circle must not hide a later failed save.
        vaultStore.loadVault(nextVault, nextRevisions, true);

        // Persist locally (full vault, not server-filtered)
        await saveLocalVault(nextVault, nextRevisions, true);
        return true;
      } else if (setResponse.status === 'error' && setResponse.error?.code === 'CONFLICT') {
        continue;
      } else {
        throw new Error(setResponse.error?.message || 'Failed to save vault');
      }

    } catch (error) {
      console.error(`Save vault attempt ${attempts}/${maxRetries} failed:`, error);

      if (attempts >= maxRetries) {
        console.error('Max retries reached, save failed');
        return false;
      }

      // Wait before retry with exponential backoff
      await new Promise(resolve => setTimeout(resolve, 1000 * attempts));
    }
  }

  return false;
}

/**
 * Refresh vault from server with conflict resolution
 * Fetches latest vault and merges with local changes
 *
 * @param deviceId - Current device ID
 * @param privateKeyBytes - Device private key for signing
 * @param masterKey - Master key for decryption
 * @returns true if successful, false if failed
 */
export async function refreshVaultWithMerge(
  identityId: string,
  _serverId: string,
  serverUrl: string,
  deviceId: string,
  signingKey: SigningKey,
  vaultKey: CryptoKey
): Promise<boolean> {
  try {
    const vaultStore = useVaultStore.getState();
    const localVault = vaultStore.vaultData;
    const localSyncPending = vaultStore.isSyncDirty;
    const localRevision = vaultStore.revisionsByIdentityId[identityId] || 0;

    // Fetch latest vault from server
    const getRequest = await signRequestWithKey('vault:get', {}, deviceId, signingKey);
    const getResponse = await vaultAPI.get(getRequest, serverUrl);

    if (getResponse.status === 'error') {
      throw new Error(getResponse.error?.message || 'Failed to fetch vault');
    }

    const serverRevision = getResponse.result?.revision || 0;

    // Check if server has data
    if (!getResponse.result?.vault) {
      return true;
    }

    // Check if already up to date
    if (serverRevision === localRevision) {
      return true;
    }

    // Decrypt server vault
    const decryptedServerVault = await decryptWithMasterKey(
      vaultKey,
      getResponse.result.vault
    );
    const serverVault: VaultData = JSON.parse(decryptedServerVault);

    // Merge with local vault if exists
    const mergedVault = localVault
      ? mergeVaultData(localVault, serverVault)
      : serverVault;

    // Update local store (preserving localData)
    const mergedWithLocalData = {
      ...mergedVault,
      localData: localVault?.localData || {}
    };

    const nextRevisions = {
      ...vaultStore.revisionsByIdentityId,
      [identityId]: serverRevision
    };
    vaultStore.loadVault(mergedWithLocalData, nextRevisions, localSyncPending);

    await saveLocalVault(mergedWithLocalData, nextRevisions, localSyncPending);

    // If we had local sync changes, mark as dirty so user can save
    return true;

  } catch (error) {
    if (error instanceof APIError && error.status === 0) {
      return false;
    }
    console.error('Failed to refresh vault:', error);
    return false;
  }
}

export async function loadLocalVaultIntoStore(): Promise<boolean> {
  const stored = await loadLocalVault();
  if (!stored?.vault) return false;

  let revisionsByIdentityId = stored.revisionsByIdentityId || {};

  // Migration: old format stored revisions keyed by serverId. Map to identityId using
  // identities already loaded in authStore (auth initializes before vault load).
  if (!stored.revisionsByIdentityId && stored.revisionsByServerId) {
    const identities = Object.values(useAuthStore.getState().identities) as Identity[];
    const serverToIdentityId = new Map(identities.map((i) => [i.serverId, i.identityId]));
    revisionsByIdentityId = {};
    for (const [serverId, rev] of Object.entries(stored.revisionsByServerId)) {
      const identityId = serverToIdentityId.get(serverId);
      if (identityId) revisionsByIdentityId[identityId] = rev;
    }
  }

  const vaultStore = useVaultStore.getState();
  vaultStore.loadVault(stored.vault, revisionsByIdentityId, stored.syncPending === true);
  return true;
}

type ServerSyncContext = {
  identityId: string;
  serverId: string;
  serverUrl: string;
  deviceId: string;
  signingKey: SigningKey;
  vaultKey: CryptoKey;
};

export function buildServerSyncContexts(
  identities: Identity[],
  devices: Device[],
  onlyServerIds?: ReadonlySet<string>
): ServerSyncContext[] {
  const serverContexts: ServerSyncContext[] = [];

  for (const identity of identities) {
    if (!identity.serverId || !identity.serverUrl) continue;
    if (onlyServerIds && !onlyServerIds.has(identity.serverId)) continue;
    const device = devices.find((d) => d.identityId === identity.identityId);
    if (!device) continue;

    serverContexts.push({
      identityId: identity.identityId,
      serverId: identity.serverId,
      serverUrl: identity.serverUrl,
      deviceId: device.deviceId,
      signingKey: device.signingKey,
      vaultKey: identity.vaultKey
    });
  }

  return serverContexts;
}

export async function refreshVaultFromAllServers(
  servers: ServerSyncContext[]
): Promise<{ ok: number; failed: number; failedContexts: ServerSyncContext[] }> {
  let ok = 0;
  let failed = 0;
  const failedContexts: ServerSyncContext[] = [];

  for (const s of servers) {
    const res = await refreshVaultWithMerge(
      s.identityId,
      s.serverId,
      s.serverUrl,
      s.deviceId,
      s.signingKey,
      s.vaultKey
    );
    if (res) ok++;
    else {
      failed++;
      failedContexts.push(s);
    }
  }

  return { ok, failed, failedContexts };
}

// ---------------------------------------------------------------------------
// Serialised save with coalescing
//
// Problem: concurrent callers would race on vaultStore mutations and on the
// markSyncClean() call, potentially losing dirty-state tracking.
//
// Solution: while a save is active, additional calls are coalesced into a
// single pending slot.  The pending slot stores only the *latest* servers
// list (the most current snapshot) and collects all waiting Promise resolvers.
// When the active save finishes, exactly one follow-up save is started; its
// result is forwarded to every coalesced caller.
// ---------------------------------------------------------------------------

let _saveActive = false;
let _pending: {
  servers: ServerSyncContext[];
  callbacks: Array<(r: { ok: number; failed: number }) => void>;
} | null = null;

async function _doSaveVaultToAllServers(
  servers: ServerSyncContext[]
): Promise<{ ok: number; failed: number }> {
  let ok = 0;
  let failed = 0;
  for (const s of servers) {
    const res = await saveVaultWithConflictResolution(
      s.identityId,
      s.serverId,
      s.serverUrl,
      s.deviceId,
      s.signingKey,
      s.vaultKey
    );
    if (res) ok++;
    else failed++;
  }
  if (servers.length > 0 && failed === 0) {
    useVaultStore.getState().markSyncClean();
  }
  return { ok, failed };
}

export async function saveVaultToAllServersWithConflictResolution(
  servers: ServerSyncContext[]
): Promise<{ ok: number; failed: number }> {
  // No active save — start one immediately.
  if (!_saveActive) {
    _saveActive = true;
    try {
      return await _doSaveVaultToAllServers(servers);
    } finally {
      _saveActive = false;
      // Flush coalesced callers with a single follow-up save.
      if (_pending) {
        const pending = _pending;
        _pending = null;
        // Kicks off recursively but _saveActive is now false, so it enters
        // the active branch above — no infinite recursion.
        saveVaultToAllServersWithConflictResolution(pending.servers)
          .then(r => pending.callbacks.forEach(cb => cb(r)))
          .catch(() =>
            pending.callbacks.forEach(cb => cb({ ok: 0, failed: pending.servers.length }))
          );
      }
    }
  }

  // A save is running — register as part of the pending slot.
  return new Promise(resolve => {
    if (!_pending) {
      _pending = { servers, callbacks: [resolve] };
    } else {
      // Coalesce: latest servers wins (reads fresh vault from store anyway),
      // but every waiting caller gets the result.
      _pending.servers = servers;
      _pending.callbacks.push(resolve);
    }
  });
}

/**
 * Record this device's master key status in the vault so all peer devices
 * in every circle can see it. Call this after creating, receiving, or
 * deleting the master key.
 *
 * The update is written to the in-memory vault store and persisted locally.
 * The caller is responsible for triggering a full vault save to the server
 * (e.g. via saveVaultToAllServersWithConflictResolution) after calling this.
 */
export function recordMasterKeyStatus(
  deviceId: string,
  status: MasterKeyStatus
): void {
  const vaultStore = useVaultStore.getState();
  const vault = vaultStore.vaultData;
  if (!vault) return;

  const entry: VaultDeviceEntry = {
    masterKeyStatus: status,
    updatedAt: new Date().toISOString()
  };

  vaultStore.loadVault({
    ...vault,
    devices: {
      ...vault.devices,
      [deviceId]: entry
    }
  }, vaultStore.revisionsByIdentityId);
  vaultStore.markSyncDirty();
}

/** Write a master key transfer request to the vault (requester side). */
export function writeMkTransferRequest(requestingDeviceId: string, code: string, ecdhPublicKey: string): void {
  const vaultStore = useVaultStore.getState();
  const vault = vaultStore.vaultData;
  if (!vault) return;

  const req: VaultMkTransferRequest = {
    ecdhPublicKey,
    code,
    requestedAt: new Date().toISOString()
  };

  vaultStore.loadVault({
    ...vault,
    pendingMkRequests: { ...vault.pendingMkRequests, [requestingDeviceId]: req }
  }, vaultStore.revisionsByIdentityId);
  vaultStore.markSyncDirty();
}

/** Write a master key transfer response to the vault (approver side). */
export function writeMkTransferResponse(
  requestingDeviceId: string,
  encryptedBlob: EncryptedBlob
): void {
  const vaultStore = useVaultStore.getState();
  const vault = vaultStore.vaultData;
  if (!vault) return;

  const resp: VaultMkTransferResponse = {
    encryptedBlob,
    respondedAt: new Date().toISOString()
  };

  // Clear the request and write the response atomically.
  const pendingMkRequests = { ...vault.pendingMkRequests };
  delete pendingMkRequests[requestingDeviceId];

  vaultStore.loadVault({
    ...vault,
    pendingMkRequests,
    pendingMkResponses: { ...vault.pendingMkResponses, [requestingDeviceId]: resp }
  }, vaultStore.revisionsByIdentityId);
  vaultStore.markSyncDirty();
}

/** Clear a completed (or cancelled) master key transfer response. */
export function clearMkTransferResponse(requestingDeviceId: string): void {
  const vaultStore = useVaultStore.getState();
  const vault = vaultStore.vaultData;
  if (!vault) return;

  const pendingMkResponses = { ...vault.pendingMkResponses };
  delete pendingMkResponses[requestingDeviceId];

  vaultStore.loadVault({
    ...vault,
    pendingMkResponses
  }, vaultStore.revisionsByIdentityId);
  vaultStore.markSyncDirty();
}

/** Record the winner of a master key conflict resolution. */
export function recordMkConflictWinner(winnerDeviceId: string): void {
  const vaultStore = useVaultStore.getState();
  const vault = vaultStore.vaultData;
  if (!vault) return;

  vaultStore.loadVault({
    ...vault,
    mkConflictResolution: {
      winnerDeviceId,
      resolvedAt: new Date().toISOString()
    }
  }, vaultStore.revisionsByIdentityId);
  vaultStore.markSyncDirty();
}

/** Clear conflict resolution entry from vault (e.g. after the losing device has acted). */
export function clearMkConflictResolution(): void {
  const vaultStore = useVaultStore.getState();
  const vault = vaultStore.vaultData;
  if (!vault) return;

  const next = { ...vault };
  delete next.mkConflictResolution;
  vaultStore.loadVault(next, vaultStore.revisionsByIdentityId);
  vaultStore.markSyncDirty();
}

/**
 * Refresh server-owned circle metadata.
 *
 * Local custom circle names live in the vault and still win when rendering labels.
 * This updates the server-provided base name plus owner info best-effort.
 */
export async function refreshCircleOwnerInfo(servers: ServerSyncContext[]): Promise<void> {
  const vaultStore = useVaultStore.getState();
  if (!vaultStore.vaultData) return;

  let changed = false;
  const authStore = useAuthStore.getState();
  for (const ctx of servers) {
    try {
      const signed = await signRequestWithKey('config:server-name', {}, ctx.deviceId, ctx.signingKey);
      const resp = await configAPI.getServerName(signed, ctx.serverUrl);
      if (resp.status === 'ok' && resp.result?.serverName) {
        const currentIdentity = useAuthStore.getState().identities[ctx.identityId];
        const nextServerName = resp.result.serverName.trim();
        if (currentIdentity && nextServerName && currentIdentity.serverName !== nextServerName) {
          authStore.updateIdentity(ctx.identityId, { serverName: nextServerName });
          await updateIdentityMeta(ctx.identityId, { serverName: nextServerName });
        }
      }
      if (resp.status === 'ok' && resp.result?.ownerIdentityId) {
        const currentStore = useVaultStore.getState();
        const nextOwnerName = resp.result.ownerIdentityName || null;
        if (
          currentStore.getCircleOwnerIdentityId(ctx.identityId) !== resp.result.ownerIdentityId ||
          currentStore.getCircleOwnerIdentityName(ctx.identityId) !== nextOwnerName
        ) {
          currentStore.setCircleOwnerIdentityId(ctx.identityId, resp.result.ownerIdentityId);
          currentStore.setCircleOwnerIdentityName(ctx.identityId, nextOwnerName);
          changed = true;
        }
      }
    } catch {
      // ignore per-server failures
    }
  }

  if (changed) {
    const authState = useAuthStore.getState();
    const allContexts = buildServerSyncContexts(
      Object.values(authState.identities),
      Object.values(authState.devices)
    );
    if (allContexts.length > 0) {
      void saveVaultToAllServersWithConflictResolution(allContexts);
    }
  }
}
